import type { ReactNode } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import type { TNavbarItemElement } from '@shared/dream-components';
import { NavbarMenuItemV2 as NavbarMenuItemElement } from '@shared/dream-components';

import { usePageContext } from '~/context/usePageContext';
import type { RootLoaderData } from '~/root';

import { getClientSideNavigationHandler, updateNodeAttributes } from '../utils/getNodeActionAndVisibility';
import { useIsLinkActive } from '../utils/useIsLinkActive';

interface Props {
  node: TNavbarItemElement;
  children: ReactNode | ReactNode[];
  screen: 'desktop' | 'mobile';
}

const ClientNavbarMenuItem = ({ node, children, screen }: Props) => {
  const { subscription } = usePageContext();
  const isPremiumUser = subscription?.tier === 'premium';
  const { isLoggedIn } = useRouteLoaderData<RootLoaderData>('root') || {};
  const supportsActiveState = node.attrs?.type === 'link' || node.attrs?.type === 'button';
  const isActive = useIsLinkActive(supportsActiveState ? node.attrs?.href : undefined, node.attrs?.isCustomLink);

  const updatedNode = updateNodeAttributes(node, Boolean(isLoggedIn), isPremiumUser, subscription);
  const handleClientSideNavigation = getClientSideNavigationHandler(updatedNode, subscription);

  return (
    <NavbarMenuItemElement
      element={updatedNode}
      controlbyselected="false"
      screen={screen}
      onClick={handleClientSideNavigation}
      isActive={isActive}
    >
      {children}
    </NavbarMenuItemElement>
  );
};

const NavbarMenuItem = ({ node, children, screen }: Props) => {
  return (
    <ClientNavbarMenuItem node={node} screen={screen}>
      {children}
    </ClientNavbarMenuItem>
  );
};

export default NavbarMenuItem;
